import React from 'react';
import { Outlet } from 'react-router-dom';
import { Box } from '@mui/material';
import AppNav, { PHONE_BOTTOM_NAV_CLEARANCE } from './AppNav';
import { useUsage } from '../../hooks/useUsage';
import { RecordingGuardProvider } from '../../contexts/RecordingGuardContext';
import { ScribeNavProvider } from '../../contexts/ScribeNavContext';

interface LayoutProps {
  userName: string;
  onSignOut: () => void;
  /** False when the user cannot reach Scribe (trial expired): the navigation then has no tool items or bottom bar. */
  showTools?: boolean;
}

export default function Layout({ userName, onSignOut, showTools = true }: LayoutProps) {
  const { usage, isLoading: usageLoading } = useUsage();

  return (
    // The guard wraps the navigation and the routed page: the recorder (inside the page)
    // reports into it and the navigation asks it before leaving. ScribeNavProvider shares the
    // active Scribe tool between the navigation and ScribeMain.
    <RecordingGuardProvider>
      <ScribeNavProvider>
        {/* Rail beside the content from sm up; slim header, content, then a fixed bottom bar on phones.
            No 100vw anywhere: the content is the flex remainder (minWidth 0), so nothing overflows sideways. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            minHeight: '100vh',
            '@supports (height: 100dvh)': { minHeight: '100dvh' },
            backgroundColor: 'var(--bg)',
          }}
        >
          <AppNav userName={userName} onSignOut={onSignOut} usage={usage} usageLoading={usageLoading} showTools={showTools} />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              minHeight: 0,
              position: 'relative',
              overflowY: 'auto',
              pb: { xs: showTools ? PHONE_BOTTOM_NAV_CLEARANCE : 0, sm: 0 },
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </ScribeNavProvider>
    </RecordingGuardProvider>
  );
}
